'use client';

import { useState, useCallback, useRef } from 'react';
import { useToast } from '@/components/shared/ui/toast';
import { batchInvalidate, type InvalidationType } from './mutations';

/**
 * Configuration for toggle mutation hooks.
 */
export interface UseToggleMutationOptions<TData> {
  /**
   * The mutation function (API call).
   * Receives the new toggle value and returns a promise.
   */
  mutationFn: (value: boolean) => Promise<TData>;

  /**
   * Business ID for cache invalidation.
   */
  businessId: string;

  /**
   * Cache invalidation types to trigger after success.
   */
  invalidate: InvalidationType[];

  /**
   * Current toggle value (for optimistic rollback).
   */
  currentValue: boolean;

  /**
   * Success message to show in toast (optional).
   * Can be a string or function that receives the new value.
   */
  successMessage?: string | ((value: boolean) => string);

  /**
   * Error message to show in toast.
   */
  errorMessage?: string | ((error: Error) => string);

  /**
   * Callback after successful mutation.
   */
  onSuccess?: (data: TData) => void;

  /**
   * Callback on error.
   */
  onError?: (error: Error) => void;

  /**
   * Delay before showing success feedback (ms).
   * Default: 1000ms (1s) for instant feel.
   */
  feedbackDelay?: number;

  /**
   * Optimistic update function.
   * Called immediately to update the UI.
   */
  optimisticUpdate: (value: boolean) => void;
}

export interface UseToggleMutationReturn {
  /**
   * Execute the toggle mutation.
   * Automatically handles optimistic update and rollback.
   */
  toggle: () => Promise<void>;

  /**
   * Whether the mutation is currently in progress.
   */
  isLoading: boolean;

  /**
   * The last error that occurred.
   */
  error: Error | null;
}

/**
 * Hook for toggle mutations with optimistic UI.
 *
 * Flow:
 * 1. User toggles switch
 * 2. Apply optimistic update immediately (UI toggles)
 * 3. Wait 1s for smooth UX
 * 4. Show success toast (optional)
 * 5. Fire API call in background
 * 6. On success: Invalidate caches
 * 7. On error: Rollback toggle + error message
 *
 * @example
 * ```tsx
 * const [ownerIsBookable, setOwnerIsBookable] = useState(false);
 *
 * const { toggle, isLoading } = useToggleMutation({
 *   mutationFn: (value) => updateBusinessSettings({ owner_is_bookable_barber: value }),
 *   businessId,
 *   currentValue: ownerIsBookable,
 *   invalidate: ['staff'],
 *   successMessage: (value) => value ? 'Owner is now bookable' : 'Owner removed from bookable staff',
 *   optimisticUpdate: (value) => setOwnerIsBookable(value),
 * });
 *
 * // In your toggle component:
 * <Switch checked={ownerIsBookable} onCheckedChange={toggle} disabled={isLoading} />
 * ```
 */
export function useToggleMutation<TData>(
  options: UseToggleMutationOptions<TData>
): UseToggleMutationReturn {
  const {
    mutationFn,
    businessId,
    invalidate,
    currentValue,
    successMessage,
    errorMessage = 'Toggle failed. Please try again.',
    onSuccess,
    onError,
    feedbackDelay = 1000,
    optimisticUpdate,
  } = options;

  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const loadingRef = useRef(false);

  const toggle = useCallback(async (): Promise<void> => {
    // Prevent concurrent mutations using ref for synchronous check
    if (loadingRef.current) return;

    loadingRef.current = true;
    setIsLoading(true);
    setError(null);

    const newValue = !currentValue;

    try {
      // Apply optimistic update immediately
      optimisticUpdate(newValue);

      // Start API call in background
      const mutationPromise = mutationFn(newValue);

      // Wait for feedback delay before showing success
      await new Promise((resolve) => setTimeout(resolve, feedbackDelay));

      // Show success toast (optional)
      if (successMessage) {
        const message =
          typeof successMessage === 'function'
            ? successMessage(newValue)
            : successMessage;
        addToast({ message, type: 'success' });
      }

      // Wait for mutation to complete
      const data = await mutationPromise;

      // Call success callback
      onSuccess?.(data);

      // Invalidate caches in background
      setTimeout(() => {
        batchInvalidate(businessId, invalidate);
      }, 100);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);

      // Rollback optimistic update
      optimisticUpdate(currentValue);

      // Show error toast
      const message =
        typeof errorMessage === 'function' ? errorMessage(error) : errorMessage;
      addToast({ message, type: 'error' });

      // Call error callback
      onError?.(error);

      // Don't re-throw - UI already rolled back
    } finally {
      loadingRef.current = false;
      setIsLoading(false);
    }
  }, [
    isLoading,
    currentValue,
    mutationFn,
    businessId,
    invalidate,
    successMessage,
    errorMessage,
    onSuccess,
    onError,
    feedbackDelay,
    optimisticUpdate,
    addToast,
  ]);

  return {
    toggle,
    isLoading,
    error,
  };
}
